import type { UIMessage } from "ai";

/**
 * Module-level cache of hydrated thread transcripts, keyed by thread id.
 *
 * The pane and full-page `/thread/$id` experiences each mount their own
 * `useChat` instance. When one unmounts and the other mounts (expand/collapse,
 * or opening a thread from the sidebar), the new instance would otherwise start
 * empty and only populate after an async `trpc.ai.messages` fetch — a visible
 * flash. Seeding the new instance from this cache lets the destination render
 * fully populated on its first paint; the background fetch then reconciles.
 *
 * This is intentionally not persisted — it only needs to survive a component
 * unmount within a session, not a reload.
 */
const cache = new Map<string, UIMessage[]>();

/** Returns the cached transcript for a thread, or `undefined` if none. */
export const getCachedMessages = (id: string): UIMessage[] | undefined =>
  cache.get(id);

/** Stores the latest known transcript for a thread. */
export const setCachedMessages = (id: string, messages: UIMessage[]): void => {
  cache.set(id, messages);
};

/** Drops a thread's cached transcript (e.g. after deletion). */
export const clearCachedMessages = (id: string): void => {
  cache.delete(id);
};
